// Parse PhotoRoom creative naming into structured tags
export function parseNomenclature(rawName) {
  if (!rawName || typeof rawName !== 'string') return {};
  // [DC]- sits at the front of the creative-name block (batch marker, manual). Read it, then parse the rest without it.
  const dcPrefix = /\[DC\]-/.test(rawName);
  const name = rawName.replace(/\[DC\]-/g, '');
  const p = {};
  let m;
  m = name.match(/^(\d+)_/);
  p.metaSeqId = m ? m[1] : null;
  // Creator block is `<type>-<Name>` (influencer-, freelancer-, …): any lowercase type is accepted.
  // Anchored first (optional `<metaId>_<owner>_` prefix), legacy `influencer-` search as fallback.
  // `internal_<Name>-<n>_…` (taxonomy example): type `internal`, no creator block.
  m = name.match(/^(?:\d+_([a-zA-Z]+)_)?internal_/);
  if (m) { p.owner = m[1] ? m[1].toLowerCase() : null; p.creatorType = 'internal'; p.creator = null; }
  else {
  m = name.match(/^(?:\d+_([a-zA-Z]+)_)?([a-z]+)-([^_\-]+(?:_[^_\-]+)?)/);
  if (m) { p.owner = m[1] ? m[1].toLowerCase() : null; p.creatorType = m[2]; p.creator = m[3]; }
  else {
    m = name.match(/_?([a-zA-Z]+)_influencer/);
    p.owner = m ? m[1].toLowerCase() : null;
    m = name.match(/influencer-([^_\-]+(?:_[^_\-]+)?)/);
    p.creator = m ? m[1] : null;
    p.creatorType = m ? 'influencer' : null;
  }
  }
  m = name.match(/[_\-](any|\d+)_HOOK/);
  p.hook = m ? m[1] : null;
  m = name.match(/_(any|[a-z]+)_VO/);
  p.vo = m ? m[1] : null;
  m = name.match(/_(any|[a-z]+)_MUSIC/);
  p.music = m ? m[1] : null;
  // Every feature the taxonomy knows (21) plus the legacy aliases. Hiding a feature in the Export picker never changes parsing.
  m = name.match(/\[(ANIM-REELS|ANIM|BIS|BATCH|MIX|BUNDLE|IMGT-[A-Z]+|AIBG|IGSTORY|IGPOST|LIPOST|LOGO|AIFILL|RETOUCH|CATALOG|RnD|GHOST|VIRTUAL|STAGING|BG|FLATLAY)\]/);
  p.feature = m ? m[1] : null;
  m = name.match(/\[([a-z]{2})\]/);
  p.lang = m ? m[1] : null;
  m = name.match(/_(\d{8})$/);
  p.dateCreated = m ? m[1] : null;
  m = name.match(/DUR-(\d+)/);
  p.duration = m ? m[1] : null;
  // v1 `PERSONA-<x>` (history) and v2 `P-<x>`; a skipped v2 tag is `-` and parses as null.
  m = name.match(/_(?:PERSONA|P)-([a-zA-Z]+)/);
  p.persona = m ? m[1] : null;
  // v2 aspect-ratio block: `PO` (portrait) or `STATIC-<dim>`
  m = name.match(/_(PO|STATIC-[A-Za-z0-9]+)(?=_|\.|$)/);
  p.dim = m ? m[1] : null;
  // Hook: ugc|aigc since 2026-09-16. Legacy names carry HOOK-lofi-ugc etc. — read only, never rewritten.
  // hookType is the normalised kind; hookRaw is exactly what the name says.
  m = name.match(/_HOOK-([A-Za-z]+(?:-[A-Za-z]+)*)(?=_|\.|$)/);
  p.hookRaw = m ? m[1] : null;
  p.hookType = m ? (/(^|-)aigc$/i.test(m[1]) ? 'aigc' : /(^|-)ugc$/i.test(m[1]) ? 'ugc' : null) : null;
  // Brackets: [DC] is a per-BATCH marker (isDC), never a feature. The feature is the last bracketed block
  // before [lang]. `feature` below stays on the known list; featureRaw is the taxonomy rule, unfiltered.
  const brackets = [...name.matchAll(/\[([^\]]+)\]/g)].map(b => b[1]);
  const langIdx = brackets.map(b => /^[a-z]{2}$/.test(b)).lastIndexOf(true);
  const beforeLang = (langIdx === -1 ? brackets : brackets.slice(0, langIdx)).filter(b => b !== 'DC');
  p.isDC = dcPrefix || brackets.includes('DC');
  p.featureRaw = beforeLang.length ? beforeLang[beforeLang.length - 1] : null;
  m = name.match(/_CID-(TB[0-9A-Z]{6})/);
  p.creativeId = m ? m[1] : null;
  return p;
}

export function detectPhase(adsetName) {
  if (!adsetName || typeof adsetName !== 'string') return 'other';
  const n = adsetName.toUpperCase();
  // v4.00 — aligné sur categorizeAdset : tout token TEST (incl. WEEKLY_TEST) => testing, en premier.
  if (n.includes('TEST')) return 'testing';
  if (n.includes('BAU') || n.includes('TIER1') || n.includes('[BIS]') || n.includes('MAIN') || n.includes('ITER')) return 'main';
  return 'other';
}

export const STUDIO_FEATURES = [
  { key: 'ANIM',    label: 'Animated Design',   aliases: ['ANIM'] },
  { key: 'GHOST',   label: 'Ghost Mannequin',   aliases: ['GHOST', 'IMGT-GHOST'] },
  { key: 'VIRTUAL', label: 'Virtual Model',     aliases: ['VIRTUAL'] },
  { key: 'STAGING', label: 'Product Staging',   aliases: ['STAGING'] },
  { key: 'AIBG',    label: 'AI Background',     aliases: ['AIBG', 'BG'] },
  { key: 'FLATLAY', label: 'Flat Lay',          aliases: ['FLATLAY'] },
];

export function studioFeatureKey(rawFeature) {
  if (!rawFeature) return null;
  for (const f of STUDIO_FEATURES) {
    if (f.aliases.includes(rawFeature)) return f.key;
  }
  return null;
}

// v2 names (Creative Name Generator) carry no CAT- block by design: a category is not "missing" on them.
// Matches `HOOK-ugc|aigc_DUR-xx_` followed by `P-<persona>_` or the bare `-_` placeholder.
export function isV2Name(name) {
  return /_HOOK-(?:ugc|aigc)_DUR-\d+_(?:P-[A-Za-z]+|-)_/.test(name || '');
}
